<script setup>
// Aba Negocios do Painel geral: monta os relatorios do Kanban que ja existem
// (mesma API, mesmos componentes) no periodo do topo do Painel. Produtos e
// negocios parados sao por funil; o resto vale para todos os funis.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { subDays } from 'date-fns';
import { getUnixStartOfDay, getUnixEndOfDay } from 'shared/helpers/DateHelper';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import { useKanbanReports } from 'kanban/composables/useKanbanReports';
import {
  ALL_BOARDS_VALUE,
  DEFAULT_TIMESERIES_METRIC,
  DEFAULT_GROUP_BY,
} from 'kanban/constants/reports';
import KanbanSummaryCards from 'kanban/components/reports/KanbanSummaryCards.vue';
import KanbanThroughputChart from 'kanban/components/reports/KanbanThroughputChart.vue';
import KanbanAgentLeaderboard from 'kanban/components/reports/KanbanAgentLeaderboard.vue';
import KanbanDimensionCharts from 'kanban/components/reports/KanbanDimensionCharts.vue';
import KanbanAgingTable from 'kanban/components/reports/KanbanAgingTable.vue';
import KanbanBoardRequiredState from 'kanban/components/reports/KanbanBoardRequiredState.vue';

const props = defineProps({
  range: { type: String, default: '7d' },
  boards: { type: Array, default: () => [] },
});

const { t, locale } = useI18n();

const RANGE_DAYS = { today: 1, '7d': 7, '30d': 30 };
const CARD_CLASS = '!rounded-2xl !border-n-weak !bg-white dark:!bg-n-solid-2';

// Com um funil so, ja entra nele: produtos e parados aparecem sem clique.
const selectedBoardId = ref(
  props.boards.length === 1 ? String(props.boards[0].id) : ALL_BOARDS_VALUE
);
const boardId = computed(() =>
  selectedBoardId.value === ALL_BOARDS_VALUE ? null : selectedBoardId.value
);
const boardOptions = computed(() => [
  { label: t('KANBAN.REPORTS.ALL_BOARDS'), value: ALL_BOARDS_VALUE },
  ...props.boards.map(board => ({
    label: board.name,
    value: String(board.id),
  })),
]);
const selectedBoardLabel = computed(
  () =>
    boardOptions.value.find(option => option.value === selectedBoardId.value)
      ?.label
);

const metric = ref(DEFAULT_TIMESERIES_METRIC);
const groupBy = ref(DEFAULT_GROUP_BY);
const agingPage = ref(1);

const params = computed(() => {
  const days = RANGE_DAYS[props.range] || 7;
  const base = {
    since: getUnixStartOfDay(subDays(new Date(), days - 1)),
    until: getUnixEndOfDay(new Date()),
  };
  if (boardId.value) base.board_id = boardId.value;
  return base;
});

const {
  reports,
  fetchSummary,
  fetchTimeseries,
  fetchAgentPerformance,
  fetchDimension,
  fetchAging,
} = useKanbanReports();

const loadTimeseries = () =>
  fetchTimeseries({
    ...params.value,
    metric: metric.value,
    group_by: groupBy.value,
  });
const loadDimension = () =>
  fetchDimension({ ...params.value, dimension: 'product' });
const loadAging = () => fetchAging({ ...params.value, page: agingPage.value });

const load = () => {
  fetchSummary(params.value);
  loadTimeseries();
  fetchAgentPerformance(params.value);
  if (!boardId.value) return;
  loadDimension();
  loadAging();
};

watch(params, () => {
  agingPage.value = 1;
  load();
});
load();

const onMetricChange = value => {
  metric.value = value;
  loadTimeseries();
};
const onGroupByChange = value => {
  groupBy.value = value;
  loadTimeseries();
};
const onAgingPage = page => {
  agingPage.value = page;
  loadAging();
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-if="boards.length > 1" class="flex justify-end">
      <SelectMenu
        :model-value="selectedBoardId"
        :options="boardOptions"
        :label="selectedBoardLabel"
        icon="i-lucide-kanban"
        sub-menu-position="bottom"
        max-width="max-w-56"
        @update:model-value="selectedBoardId = String($event)"
      />
    </div>

    <KanbanSummaryCards
      :class="CARD_CLASS"
      :data="reports.summary.data"
      :is-loading="reports.summary.isLoading"
      :error="reports.summary.error"
      :locale="locale"
      @retry="fetchSummary(params)"
    />
    <KanbanThroughputChart
      :class="CARD_CLASS"
      :data="reports.timeseries.data"
      :is-loading="reports.timeseries.isLoading"
      :error="reports.timeseries.error"
      :metric="metric"
      :group-by="groupBy"
      :locale="locale"
      @retry="loadTimeseries"
      @update:metric="onMetricChange"
      @update:group-by="onGroupByChange"
    />

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <KanbanAgentLeaderboard
        :class="CARD_CLASS"
        :data="reports.agentPerformance.data"
        :is-loading="reports.agentPerformance.isLoading"
        :error="reports.agentPerformance.error"
        :locale="locale"
        @retry="fetchAgentPerformance(params)"
      />
      <KanbanBoardRequiredState
        v-if="!boardId"
        :class="CARD_CLASS"
        :boards="boards"
        @select="selectedBoardId = String($event)"
      />
      <KanbanDimensionCharts
        v-else
        :class="CARD_CLASS"
        :title="t('VINCTO_HOME.DEALS.PRODUCTS_TITLE')"
        :info-text="t('VINCTO_HOME.DEALS.PRODUCTS_INFO')"
        :data="reports.dimension.data"
        :is-loading="reports.dimension.isLoading"
        :error="reports.dimension.error"
        :locale="locale"
        @retry="loadDimension"
      />
    </div>

    <KanbanAgingTable
      v-if="boardId"
      :class="CARD_CLASS"
      :data="reports.aging.data"
      :is-loading="reports.aging.isLoading"
      :error="reports.aging.error"
      :locale="locale"
      @retry="loadAging"
      @update:page="onAgingPage"
    />

    <router-link
      :to="{ name: 'kanban_reports' }"
      class="self-end text-sm font-medium text-n-blue-11 hover:underline"
    >
      {{ t('VINCTO_HOME.DEALS.FULL_REPORT') }}
    </router-link>
  </div>
</template>
